import React from 'react'
import {Provider, useSelector} from 'react-redux'
import {
  Navigate,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
  useLocation
} from 'react-router-dom'
import AuthDispatcher from './AuthDispatcher'
import AccountDeletionPage from './components/AccountDeletionPage'
import AddCommunityPage from './components/AddCommunityPage'
import CommunitiesPage from './components/CommunitiesPage'
import CommunityToolSelectorPage from './components/CommunitiesPage/CommunityToolSelectorPage'
import EditCommunityPage from './components/EditCommunityPage'
import EducationalResourcesPage from './components/EducationalResourcesPage'
import EmailUnsubscribedPage from './components/email/EmailUnsubscribedPage'
import EmailUnsubscribeFailedPage from './components/email/EmailUnsubscribeFailedPage'
import EventsPage from './components/EventsPage'
import LearnerProfilePage from './components/LearnerProfilePage'
import LearnersPage from './components/LearnersPage'
import MentorProfilePage from './components/MentorProfilePage'
import SponsorManagerPage from './components/SponsorsPage/SponsorManagerPage'
import SuppliesShopPage from './components/SuppliesShopPage'
import ToolSelectorPage from './components/ToolSelectorPage'
import UserSearchPage from './components/UserSearchPage'
import {
  ACCOUNT_DELETION_ROUTE,
  ADD_COMMUNITY_ROUTE,
  CHECK_MAIL_ROUTE,
  COMMUNITIES_ROUTE,
  COMMUNITY_EVENTS_ROUTE,
  COMMUNITY_NOT_HERE_ROUTE,
  COMMUNITY_TOOL_SELECTOR_ROUTE,
  EDIT_COMMUNITY_ROUTE,
  EDUCATIONAL_RESOURCES_ROUTE,
  EMAIL_SENT_ROUTE,
  EMAIL_UNSUBSCRIBED_ROUTE,
  EMAIL_UNSUBSCRIBE_FAILED_ROUTE,
  FORGOT_PASSWORD_ROUTE,
  LEARNERS_ROUTE,
  LEARNER_PROFILE_ROUTE,
  LEGAL_TERMS_ROUTE,
  LOGIN_ROUTE,
  MENTOR_PROFILE_ROUTE,
  REGISTERED_ROUTE,
  REGISTER_LEARNER_ROUTE,
  REGISTER_ROUTE,
  RESET_PASSWORD_ROUTE,
  ROOT_ROUTE,
  SPONSOR_MANAGER_ROUTE,
  SUPPLIES_SHOP_ROUTE,
  TERMS_ROUTE,
  TOOL_SELECTOR_ROUTE,
  USER_SEARCH_ROUTE
} from './global-constants/routingConstants'
import I18N from './I18N'
import ErrorBoundary from './legacy/components/ErrorBoundary'
import ForgotPasswordPage from './legacy/components/pages/ForgotPasswordPage'
import ForgotPasswordSuccessPage from './legacy/components/pages/ForgotPasswordSuccessPage/ForgotPasswordSuccessPage.component'
import LegalTermsPage from './legacy/components/pages/LegalTermsPage'
import LoginPage from './legacy/components/pages/LoginPage'
import NotFoundPage from './legacy/components/pages/NotFoundPage'
import RegistrationPage from './legacy/components/pages/RegistrationPage'
import RegistrationSuccessPage from './legacy/components/pages/RegistrationSuccessPage/RegistrationSuccess.component'
import ResetPasswordPage from './legacy/components/pages/ResetPassword/ResetPassword'
import RootPage from './legacy/components/pages/RootPage'
import TermsAndConditionsPage from './legacy/components/pages/TermsAndConditionsPage'
import VerificationEmailSentPage from './legacy/components/pages/VerificationEmailSentPage'
import CommunityNotHerePage from './legacy/containers/CommunityNotHere.container'
import {Role} from './store/authentication/authentication.type'
import configureStore, {AppState} from './store/store'

const store = configureStore()

const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route path={ROOT_ROUTE} element={<RootPage />} />
      <Route path={REGISTER_ROUTE} element={<RegistrationPage />} />
      <Route path={REGISTERED_ROUTE} element={<RegistrationSuccessPage />} />
      <Route path={CHECK_MAIL_ROUTE} element={<ForgotPasswordSuccessPage />} />
      <Route path={EMAIL_SENT_ROUTE} element={<VerificationEmailSentPage />} />
      <Route
        path={COMMUNITY_NOT_HERE_ROUTE}
        element={<CommunityNotHerePage />}
      />
      <Route path={FORGOT_PASSWORD_ROUTE} element={<ForgotPasswordPage />} />
      <Route path={RESET_PASSWORD_ROUTE} element={<ResetPasswordPage />} />
      <Route path={LOGIN_ROUTE} element={<LoginPage />} />
      <Route path={LEGAL_TERMS_ROUTE} element={<LegalTermsPage />} />
      <Route path={TERMS_ROUTE} element={<TermsAndConditionsPage />} />
      <Route
        path={EMAIL_UNSUBSCRIBED_ROUTE}
        element={<EmailUnsubscribedPage />}
      />
      <Route
        path={EMAIL_UNSUBSCRIBE_FAILED_ROUTE}
        element={<EmailUnsubscribeFailedPage />}
      />
      <Route path={ACCOUNT_DELETION_ROUTE} element={<AccountDeletionPage />} />

      <Route
        path={TOOL_SELECTOR_ROUTE}
        element={
          <RequireAuth allowedRoles={['ROLE_MENTOR', 'ROLE_ADMIN']}>
            <ToolSelectorPage />
          </RequireAuth>
        }
      />

      <Route
        path={LEARNERS_ROUTE}
        element={
          <RequireAuth allowedRoles={['ROLE_MENTOR']}>
            <LearnersPage />
          </RequireAuth>
        }
      />

      <Route
        path={EDUCATIONAL_RESOURCES_ROUTE}
        element={
          <RequireAuth allowedRoles={['ROLE_MENTOR']}>
            <EducationalResourcesPage />
          </RequireAuth>
        }
      />

      <Route
        path={SUPPLIES_SHOP_ROUTE}
        element={
          <RequireAuth allowedRoles={['ROLE_MENTOR']}>
            <SuppliesShopPage />
          </RequireAuth>
        }
      />

      <Route
        path={REGISTER_LEARNER_ROUTE}
        element={<Navigate to={LEARNERS_ROUTE} />}
      />

      <Route
        path={COMMUNITY_TOOL_SELECTOR_ROUTE}
        element={
          <RequireAuth allowedRoles={['ROLE_ADMIN']}>
            <CommunityToolSelectorPage />
          </RequireAuth>
        }
      />

      <Route
        path={COMMUNITY_EVENTS_ROUTE}
        element={
          <RequireAuth allowedRoles={['ROLE_ADMIN']}>
            <EventsPage />
          </RequireAuth>
        }
      />

      <Route
        path={EDIT_COMMUNITY_ROUTE}
        element={
          <RequireAuth allowedRoles={['ROLE_ADMIN']}>
            <EditCommunityPage />
          </RequireAuth>
        }
      />

      <Route
        path={ADD_COMMUNITY_ROUTE}
        element={
          <RequireAuth allowedRoles={['ROLE_ADMIN']}>
            <AddCommunityPage />
          </RequireAuth>
        }
      />

      <Route
        path={COMMUNITIES_ROUTE}
        element={
          <RequireAuth allowedRoles={['ROLE_ADMIN']}>
            <CommunitiesPage />
          </RequireAuth>
        }
      />

      <Route
        path={COMMUNITY_TOOL_SELECTOR_ROUTE}
        element={
          <RequireAuth allowedRoles={['ROLE_ADMIN']}>
            <CommunityToolSelectorPage />
          </RequireAuth>
        }
      />

      <Route
        path={SPONSOR_MANAGER_ROUTE}
        element={
          <RequireAuth allowedRoles={['ROLE_ADMIN']}>
            <SponsorManagerPage />
          </RequireAuth>
        }
      />

      <Route
        path={USER_SEARCH_ROUTE}
        element={
          <RequireAuth allowedRoles={['ROLE_ADMIN']}>
            <UserSearchPage />
          </RequireAuth>
        }
      />

      <Route
        path={LEARNER_PROFILE_ROUTE}
        element={
          <RequireAuth allowedRoles={['ROLE_ADMIN']}>
            <LearnerProfilePage />
          </RequireAuth>
        }
      />

      <Route
        path={MENTOR_PROFILE_ROUTE}
        element={
          <RequireAuth allowedRoles={['ROLE_ADMIN']}>
            <MentorProfilePage />
          </RequireAuth>
        }
      />

      <Route element={<NotFoundPage />} />
    </>
  )
)

function App() {
  return (
    <Provider store={store}>
      <AuthDispatcher>
        <I18N>
          <ErrorBoundary>
            <RouterProvider router={router} />
          </ErrorBoundary>
        </I18N>
      </AuthDispatcher>
    </Provider>
  )
}

function RequireAuth({
  allowedRoles,
  children
}: Readonly<{allowedRoles: ReadonlyArray<Role>; children: JSX.Element}>) {
  const roles = useSelector((state: AppState) => state.auth.roles)
  const hasMatchingRole = allowedRoles.some(role => roles.indexOf(role) !== -1)
  const location = useLocation()

  if (!hasMatchingRole) {
    return <Navigate to={LOGIN_ROUTE} state={{from: location}} />
  }

  return children
}

export default App
